"use client"

import React from "react"
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  RadialBarChart,
  RadialBar,
  Treemap,
  Scatter,
  ScatterChart,
  Brush,
  ReferenceLine,
  Legend,
  Tooltip,
  CartesianGrid,
  XAxis,
  YAxis,
  Customized,
  Label,
  LabelList,
  ZAxis,
  FunnelChart,
  Funnel,
  ComposedChart
} from "recharts"
import { Activity, ArrowRight, BarChart2, LineChart as LineChartIcon, PieChart as PieChartIcon, ScatterChart as ScatterChartIcon, Square } from "lucide-react"

// Enhanced reusable tooltip for consistency across all chart types
const CustomTooltip = ({ active, payload, label, nameKey = "name", valueKey = "value" }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover/95 backdrop-blur-sm shadow-md rounded-md p-2 border border-border text-xs">
        <p className="font-medium text-popover-foreground mb-1">{label || payload[0].payload[nameKey]}</p>
        {payload.map((entry: any, index: number) => (
          <div key={`item-${index}`} className="flex items-center gap-2">
            <div 
              className="w-2 h-2 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="font-medium">{entry.name || entry.dataKey}:</span>
            <span className="text-popover-foreground">
              {typeof entry.value === 'number' ? 
                entry.value.toLocaleString() : 
                entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

// Types for chart data
export type ChartType = "area" | "bar" | "line" | "pie" | "radar" | "radialBar" | "treemap" | "scatter" | "barBrush" | "advancedScatter" | "bubblePlot" | "heatmap" | "funnel" | "boxplot"

export interface ChartConfig {
  title?: string
  description?: string
  xAxisKey?: string
  yAxisKey?: string // Added for heatmap charts
  yAxisKeys?: string[]
  valueKey?: string // Added for heatmap and funnel charts
  nameKey?: string // Added for funnel charts
  colors?: string[]
  variant?: string // For different chart variants: 'stacked', 'donut', etc.
  innerRadius?: number // For pie charts (donut)
  outerRadius?: number // For all radial charts
  barSize?: number // For bar charts
  stackId?: string // For stacked charts
  showLabels?: boolean // Show data labels
  labelType?: 'value' | 'percent' | 'custom' // Type of label to show
  showGrid?: boolean // Show grid lines - defaults to false for cleaner visuals
  gridType?: 'line' | 'circle' | 'none' // Type of grid (for radar charts)
  dataKey?: string // For treemap charts
  showBrush?: boolean // For charts with brush
  zAxisKey?: string // For 3D charts like bubble charts
  suggestions?: ChartSuggestion[]
  show_suggestions?: boolean // Flag to control whether to show suggestions
  name?: string // Chart name for advanced charts
  seriesData?: any[][] // For pre-grouped multi-series data
  seriesKey?: string // For explicitly specifying which field to use for series grouping
  tooltip?: {
    contentStyle?: React.CSSProperties;
    labelStyle?: React.CSSProperties;
    itemStyle?: React.CSSProperties;
    wrapperStyle?: React.CSSProperties;
    formatter?: (value: any, entry?: any, index?: number) => React.ReactNode;
    content?: (value: any, entry?: any, index?: number) => React.ReactNode;
  };
}

export interface ChartData {
  chartType: ChartType
  data: any[]
  config: ChartConfig
  suggestions?: ChartSuggestion[]
}

export interface ChartSuggestion {
  chartType: ChartType
  reason: string
  query?: string
  title?: string  // More descriptive title for the suggestion
}

// Default colors for charts - using CSS variables from global.css
const DEFAULT_COLORS = [
  "hsl(var(--chart-1) / 0.75)",
  "hsl(var(--chart-2) / 0.75)",
  "hsl(var(--chart-3) / 0.75)",
  "hsl(var(--chart-4) / 0.75)",
  "hsl(var(--chart-5) / 0.75)",
  "hsl(var(--chart-6) / 0.75)",
  "hsl(var(--chart-7) / 0.75)",
  "hsl(var(--chart-8) / 0.75)",
]

// Common utility function to transform date string values to numeric values for chart rendering
// This should be used by all chart components to ensure consistent handling of date data
const transformDateValues = (data: any[], yAxisKeys: string[], xAxisKey: string): {
  transformedData: any[];
  numericKeys: string[];
  hasDateValues: boolean;
} => {
  if (!data || data.length === 0) {
    return { transformedData: [], numericKeys: [], hasDateValues: false };
  }

  // Filter out the xAxisKey from the yAxisKeys
  const validYKeys = yAxisKeys.filter(key => key !== xAxisKey);

  // Transform data to convert date strings to numeric values
  const transformedData = data.map(item => {
    const newItem = { ...item };
    validYKeys.forEach(key => {
      // Only process if the item has this key
      if (!(key in item)) return;

      // Check if the value is a date string
      if (typeof item[key] === 'string' && 
          (item[key].includes('-') || item[key].includes('/')) && 
          !isNaN(Date.parse(item[key]))) {
        try {
          // Convert to timestamp for internal use
          const date = new Date(item[key]);
          const timestamp = date.getTime();
          newItem[`${key}_numeric`] = timestamp;
        } catch (e) {
          console.error(`Failed to convert date string: ${item[key]}`);
        }
      }
    });
    return newItem;
  });

  // Replace original keys with numeric versions for date values
  const numericKeys = validYKeys.map(key => {
    // Handle case where key doesn't exist in first item
    if (!data[0] || !(key in data[0])) return key;

    const firstValue = data[0][key];
    // Check if the first value is a date string
    if (typeof firstValue === 'string' && 
        (firstValue.includes('-') || firstValue.includes('/')) && 
        !isNaN(Date.parse(firstValue))) {
      return `${key}_numeric`;
    }
    return key;
  });

  // Check if we have any date values
  const hasDateValues = numericKeys.some(key => key.endsWith('_numeric'));

  return { transformedData, numericKeys, hasDateValues };
};

// Utility function to sort data if appropriate (dates, sequential data)
const sortDataIfNeeded = (data: any[], xAxisKey: string): any[] => {
  if (!data || data.length <= 1) return data;
  
  // Check if we should skip sorting
  if (isAlreadySorted(data, xAxisKey)) {
    return data; // Data is already in the correct order, no need to sort
  }
  
  // Clone the data to avoid mutating the original
  const clonedData = [...data];
  
  // Check if we have date values in the xAxisKey
  const firstItem = data[0][xAxisKey];
  
  // If the xAxisKey contains date-like strings or Date objects
  if (
    (typeof firstItem === 'string' && 
      (firstItem.includes('/') || 
       firstItem.includes('-') || 
       firstItem.match(/\d{4}/) // Year format
      )
    ) || 
    firstItem instanceof Date
  ) {
    // Sort by date
    return clonedData.sort((a, b) => {
      const dateA = new Date(a[xAxisKey]);
      const dateB = new Date(b[xAxisKey]);
      return dateA.getTime() - dateB.getTime();
    });
  }
  
  // If the xAxisKey contains numeric values
  if (typeof firstItem === 'number') {
    // Sort numerically
    return clonedData.sort((a, b) => a[xAxisKey] - b[xAxisKey]);
  }
  
  // Otherwise return the original data (for categorical data)
  return data; // Return original data to preserve the LLM's ordering
};

// Helper function to check if data is already properly sorted
const isAlreadySorted = (data: any[], xAxisKey: string): boolean => {
  if (!data || data.length <= 1) return true;
  
  const firstItem = data[0][xAxisKey];
  
  // For date values
  if ((typeof firstItem === 'string' && 
      (firstItem.includes('/') || firstItem.includes('-') || firstItem.match(/\d{4}/))
     ) || firstItem instanceof Date) {
    
    // Check if dates are already in ascending order
    for (let i = 1; i < data.length; i++) {
      const dateA = new Date(data[i-1][xAxisKey]);
      const dateB = new Date(data[i][xAxisKey]);
      
      // If we find any out-of-order dates, return false
      if (dateA.getTime() > dateB.getTime()) {
        return false;
      }
    }
    return true; // All dates are in order
  }
  
  // For numeric values
  if (typeof firstItem === 'number') {
    // Check if numbers are already in ascending order
    for (let i = 1; i < data.length; i++) {
      if (data[i-1][xAxisKey] > data[i][xAxisKey]) {
        return false;
      }
    }
    return true; // All numbers are in order
  }
  
  // For categorical data, always consider it "already sorted"
  return true;
};

// Type definition for formatter functions to fix TypeScript issues
type FormatterFunction = (value: any, entry?: any, index?: number) => React.ReactNode;

// Create a reusable SuggestionList component to be used across all chart types
const SuggestionList: React.FC<{ suggestions: ChartSuggestion[] }> = ({ suggestions }) => {
  if (!suggestions || suggestions.length === 0) return null;
  
  return (
    <div className="mt-4 space-y-2">
      <h4 className="text-sm font-medium text-muted-foreground mb-2">Chart Recommendations</h4>
      <div className="flex flex-wrap gap-2 mt-1">
        {suggestions.map((suggestion, i) => (
          <div 
            key={i}
            className="group hover:scale-105 hover:transition-all hover:duration-300 relative flex flex-col items-start p-3 pr-10 bg-muted/30 hover:bg-muted/60 border border-border hover:border-primary/30 rounded-md text-xs cursor-pointer transition-all hover:shadow-sm"
            title={suggestion.reason}
            onClick={() => {
              // Create a custom event to send the suggestion query
              const event = new CustomEvent('chart-suggestion-click', {
                detail: { query: suggestion.query }
              });
              window.dispatchEvent(event);
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              {suggestion.chartType === 'bar' && <BarChart2 className="h-3.5 w-3.5 text-primary/70" />}
              {suggestion.chartType === 'line' && <LineChartIcon className="h-3.5 w-3.5 text-primary/70" />}
              {suggestion.chartType === 'area' && <Activity className="h-3.5 w-3.5 text-primary/70" />}
              {suggestion.chartType === 'pie' && <PieChartIcon className="h-3.5 w-3.5 text-primary/70" />}
              {suggestion.chartType === 'treemap' && <Square className="h-3.5 w-3.5 text-primary/70" />}
              {suggestion.chartType === 'radar' && <Activity className="h-3.5 w-3.5 text-primary/70" />}
              {suggestion.chartType === 'scatter' && <ScatterChartIcon className="h-3.5 w-3.5 text-primary/70" />}
              {suggestion.chartType === 'advancedScatter' && <ScatterChartIcon className="h-3.5 w-3.5 text-primary/70" />}
              {suggestion.chartType === 'bubblePlot' && <ScatterChartIcon className="h-3.5 w-3.5 text-primary/70" />}
              {suggestion.chartType === 'barBrush' && <BarChart2 className="h-3.5 w-3.5 text-primary/70" />}
              {suggestion.chartType === 'radialBar' && <PieChartIcon className="h-3.5 w-3.5 text-primary/70" />}
              
              <span className="font-medium">{suggestion.title || `Try ${suggestion.chartType} chart`}</span>
            </div>
            <p className="text-muted-foreground text-[10px] line-clamp-2">{suggestion.reason}</p>
            
            <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
              <ArrowRight className="h-3.5 w-3.5 text-primary" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AreaChartComponent: React.FC<{ config: ChartConfig; data: any[] }> = ({ config, data }) => {
  const { 
    xAxisKey = "x", 
    yAxisKeys = ["y"], 
    colors = DEFAULT_COLORS,
    variant = "default", // 'default', 'stacked', 'expanded', 'step'
    showGrid = false,
    tooltip
  } = config
  
  // Extract suggestions if they exist in the config
  const chartSuggestions = config.suggestions || [];
  const shouldShowSuggestions = chartSuggestions.length > 0 && config.show_suggestions !== false;
  
  // Sort data if appropriate (like dates)
  const sortedData = sortDataIfNeeded(data, xAxisKey);
  
  // Determine chart type based on variant
  const chartType = variant === 'step' ? 'step' : 'monotone'
  const isStacked = variant === 'stacked' || variant === 'expanded'
  const stackOffset = variant === 'expanded' ? 'expand' : 'none'

  // Enhanced fix for yAxisKeys filtering:
  // 1. Remove xAxisKey from yAxisKeys
  // 2. Ensure only numeric columns are included
  const validYAxisKeys = yAxisKeys
    .filter(key => key !== xAxisKey)
    .filter(key => {
      // Check if this key contains numeric data
      return data.some(item => typeof item[key] === 'number' || 
                      (typeof item[key] === 'string' && !isNaN(parseFloat(item[key]))));
    });

  // If no valid keys were found after filtering, try to auto-detect numeric columns
  const fallbackYAxisKeys = validYAxisKeys.length > 0 ? validYAxisKeys : 
    Object.keys(data[0] || {})
      .filter(key => key !== xAxisKey)
      .filter(key => {
        return data.some(item => typeof item[key] === 'number' || 
                        (typeof item[key] === 'string' && !isNaN(parseFloat(item[key]))));
      });

  // Use fallback keys if needed
  const keysToUse = validYAxisKeys.length > 0 ? validYAxisKeys : fallbackYAxisKeys;

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart 
          data={sortedData} 
          margin={{ top: 20, right: 20, left: 5, bottom: 5 }}
          stackOffset={stackOffset as any}
        >
          <defs>
            {keysToUse.map((key, index) => (
              <linearGradient key={key} id={`chart-${key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors[index % colors.length]} stopOpacity={0.9} />
                <stop offset="95%" stopColor={colors[index % colors.length]} stopOpacity={0.3} />
              </linearGradient>
            ))}
          </defs>
          {showGrid && <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />}
          <XAxis
            dataKey={xAxisKey}
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tick={{ fill: "hsl(var(--muted-foreground))" }}
            tickMargin={8}
            fontSize={12}
          />
          <YAxis
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tick={{ fill: "hsl(var(--muted-foreground))" }}
            fontSize={12}
            width={60}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--background))",
              borderColor: "hsl(var(--border))",
              borderRadius: "6px",
              ...(tooltip?.contentStyle || {})
            }}
            labelStyle={{ 
              color: "hsl(var(--foreground))",
              ...(tooltip?.labelStyle || {})
            }}
            itemStyle={{ 
              color: "hsl(var(--foreground))",
              ...(tooltip?.itemStyle || {})
            }}
            wrapperStyle={tooltip?.wrapperStyle}
            formatter={tooltip?.formatter as any}
            content={tooltip?.content || CustomTooltip}
          />
          <Legend iconType="circle" wrapperStyle={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }} />
          {keysToUse.map((key, index) => (
            <Area
              key={key}
              type={chartType}
              dataKey={key}
              name={key}
              stackId={isStacked ? "1" : undefined}
              stroke={colors[index % colors.length]}
              fill={colors[index % colors.length]}
              fillOpacity={0.2}
              isAnimationActive={true}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
      
      {/* Add suggestions list */}
      {shouldShowSuggestions && <SuggestionList suggestions={chartSuggestions} />}
    </div>
  )
}

export const BarChartComponent: React.FC<{ config: ChartConfig; data: any[] }> = ({ config, data }) => {
  const { 
    xAxisKey = "category", 
    yAxisKeys = ["value"], 
    colors = DEFAULT_COLORS,
    variant = "default", // 'default', 'horizontal', 'stacked', 'negative'
    barSize = 30,
    showLabels = false,
    showGrid = false,
    tooltip
  } = config

  // Extract suggestions if they exist in the config
  const chartSuggestions = config.suggestions || [];
  const shouldShowSuggestions = chartSuggestions.length > 0 && config.show_suggestions !== false;

  // Sort data for sequential data types if needed (eg. for time series)
  const sortedData = sortDataIfNeeded(data, xAxisKey);

  // Set the layout based on variant
  const layout = variant === 'horizontal' ? 'vertical' : 'horizontal'
  // Determine if bars should be stacked
  const stackId = variant === 'stacked' ? '1' : undefined

  // Use the common utility to transform date string values to numeric values
  const { transformedData, numericKeys, hasDateValues } = transformDateValues(sortedData, yAxisKeys, xAxisKey);

  // For negative values variant
  const hasNegativeValues = variant === 'negative' || 
    transformedData.some(item => 
      numericKeys.some(key => typeof item[key] === 'number' && item[key] < 0)
    );

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={300}>
        <BarChart 
          data={transformedData} 
          margin={{ top: 20, right: 20, left: 20, bottom: 5 }}
          layout={layout as any}
        >
          {showGrid && <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} vertical={false} />}
          <XAxis
            dataKey={layout === 'vertical' ? undefined : xAxisKey}
            type={layout === 'vertical' ? 'number' : 'category'}
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tick={{ fill: "hsl(var(--muted-foreground))" }}
            tickMargin={8}
            fontSize={12}
          />
          <YAxis
            dataKey={layout === 'vertical' ? xAxisKey : undefined}
            type={layout === 'vertical' ? 'category' : 'number'}
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tick={{ fill: "hsl(var(--muted-foreground))" }}
            fontSize={12}
            width={layout === 'vertical' ? 100 : 60}
            // For date values, use a formatter to show the original dates
            tickFormatter={hasDateValues ? 
              (value) => {
                // Leave the tick formatting empty as we'll use tooltips for dates
                return '';
              } : undefined
            }
          />
          <Tooltip
            cursor={{ fill: "hsla(var(--muted), 0.1)" }}
            contentStyle={{
              backgroundColor: "hsl(var(--background))",
              borderColor: "hsl(var(--border))",
              borderRadius: "6px",
              ...(tooltip?.contentStyle || {})
            }}
            labelStyle={{ 
              color: "hsl(var(--foreground))",
              ...(tooltip?.labelStyle || {})
            }}
            itemStyle={{ 
              color: "hsl(var(--foreground))",
              ...(tooltip?.itemStyle || {})
            }}
            wrapperStyle={tooltip?.wrapperStyle}
            formatter={tooltip?.formatter as any}
            content={tooltip?.content || CustomTooltip}
          />
          <Legend 
            iconType="circle" 
            wrapperStyle={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}
            // Format legend names back to original
            formatter={(value: string | number) => {
              // Force to string and check
              const valueStr = String(value);
              if (valueStr.endsWith('_numeric')) {
                return valueStr.replace('_numeric', '');
              }
              return value;
            }}
          />
          {numericKeys.map((key, index) => {
            // Get original key name for display
            const displayKey = typeof key === 'string' && key.endsWith('_numeric') ? 
              key.replace('_numeric', '') : key;
            
            return (
              <Bar
                key={key}
                dataKey={key}
                name={key}
                fill={colors[index % colors.length]}
                stroke={colors[index % colors.length]}
                strokeWidth={1}
                radius={[4, 4, 0, 0]}
                barSize={barSize}
                stackId={stackId}
                isAnimationActive={true}
              >
                {hasNegativeValues && (
                  transformedData.map((entry, i) => (
                    <Cell
                      key={`cell-${i}`}
                      fill={entry[key] >= 0 ? colors[index % colors.length] : colors[(index + 1) % colors.length]}
                    />
                  ))
                )}
                {showLabels && (
                  <LabelList 
                    dataKey={key} 
                    position="top" 
                    fill="hsl(var(--foreground))" 
                    fontSize={12} 
                    // Format labels back to original dates if needed
                    formatter={(value: any) => {
                      if (typeof key === 'string' && key.endsWith('_numeric')) {
                        // Find the index of this item
                        const itemIndex = transformedData.findIndex(item => item[key] === value);
                        if (itemIndex >= 0) {
                          const originalKey = key.replace('_numeric', '');
                          return transformedData[itemIndex][originalKey];
                        }
                      }
                      return value;
                    }}
                  />
                )}
              </Bar>
            );
          })}
        </BarChart>
      </ResponsiveContainer>
      
      {/* Add suggestions list */}
      {shouldShowSuggestions && <SuggestionList suggestions={chartSuggestions} />}
    </div>
  )
}

export const LineChartComponent: React.FC<{ config: ChartConfig; data: any[] }> = ({ config, data }) => {
  const { 
    xAxisKey = "x", 
    yAxisKeys = ["y"], 
    colors = DEFAULT_COLORS,
    variant = "default", // 'default', 'step', 'dots'
    showLabels = false,
    showGrid = false,
    tooltip
  } = config

  // Extract suggestions if they exist in the config
  const chartSuggestions = config.suggestions || [];
  const shouldShowSuggestions = chartSuggestions.length > 0 && config.show_suggestions !== false;

  // Sort data for time series data if needed
  const sortedData = sortDataIfNeeded(data, xAxisKey);

  // Determine line type based on variant
  const lineType = variant === 'step' ? 'step' : 'monotone'
  // Determine if dots should be prominent
  const dotSize = variant === 'dots' ? 6 : 3

  // Use the common utility to transform date string values to numeric values
  const { transformedData, numericKeys, hasDateValues } = transformDateValues(sortedData, yAxisKeys, xAxisKey);

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={transformedData} margin={{ top: 20, right: 20, left: 5, bottom: 5 }}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />}
          <XAxis
            dataKey={xAxisKey}
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tick={{ fill: "hsl(var(--muted-foreground))" }}
            tickMargin={8}
            fontSize={12}
          />
          <YAxis
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tick={{ fill: "hsl(var(--muted-foreground))" }}
            fontSize={12}
            width={60}
            // For date values, use a formatter to show the original dates
            tickFormatter={hasDateValues ? 
              (value) => {
                // Leave the tick formatting empty as we'll use tooltips for dates
                return '';
              } : undefined
            }
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--background))",
              borderColor: "hsl(var(--border))",
              borderRadius: "6px",
              ...(tooltip?.contentStyle || {})
            }}
            labelStyle={{ 
              color: "hsl(var(--foreground))",
              ...(tooltip?.labelStyle || {})
            }}
            itemStyle={{ 
              color: "hsl(var(--foreground))",
              ...(tooltip?.itemStyle || {})
            }}
            wrapperStyle={tooltip?.wrapperStyle}
            formatter={tooltip?.formatter as any}
            content={tooltip?.content || CustomTooltip}
          />
          <Legend 
            iconType="circle" 
            wrapperStyle={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}
            // Format legend names back to original
            formatter={(value: string | number) => {
              // Force to string and check
              const valueStr = String(value);
              if (valueStr.endsWith('_numeric')) {
                return valueStr.replace('_numeric', '');
              }
              return value;
            }}
          />
          {numericKeys.map((key, index) => (
            <Line
              key={key}
              type={lineType}
              dataKey={key}
              name={key}
              stroke={colors[index % colors.length]}
              activeDot={{ r: dotSize + 2 }}
              strokeWidth={2}
              dot={{ r: dotSize, fill: "hsl(var(--background))", strokeWidth: 2, stroke: colors[index % colors.length] }}
              isAnimationActive={true}
            >
              {showLabels && (
                <LabelList 
                  dataKey={key} 
                  position="top" 
                  fill="hsl(var(--foreground))" 
                  fontSize={12} 
                  // Format labels back to original dates if needed
                  formatter={(value: any) => {
                    if (typeof key === 'string' && key.endsWith('_numeric')) {
                      // Find the index of this item
                      const itemIndex = transformedData.findIndex(item => item[key] === value);
                      if (itemIndex >= 0) {
                        const originalKey = key.replace('_numeric', '');
                        return transformedData[itemIndex][originalKey];
                      }
                    }
                    return value;
                  }}
                />
              )}
            </Line>
          ))}
        </LineChart>
      </ResponsiveContainer>
      
      {/* Add suggestions list */}
      {shouldShowSuggestions && <SuggestionList suggestions={chartSuggestions} />}
    </div>
  )
}

export const PieChartComponent: React.FC<{ config: ChartConfig; data: any[] }> = ({ config, data }) => {
  const { 
    colors = DEFAULT_COLORS,
    variant = "default", // 'default', 'donut', 'label', 'labelList'
    innerRadius = variant === 'donut' ? 60 : 0,
    outerRadius = 100,
    showLabels = variant === 'label' || variant === 'labelList',
    tooltip
  } = config

  // Extract suggestions if they exist in the config
  const chartSuggestions = config.suggestions || [];
  const shouldShowSuggestions = chartSuggestions.length > 0 && config.show_suggestions !== false;

  // Find the value and name keys in the data
  const valueKey = Object.keys(data[0]).find((key) => typeof data[0][key] === "number") || "value"
  const nameKey = Object.keys(data[0]).find((key) => key !== valueKey) || "name"

  // Check if data is already sorted in descending order by value
  const isAlreadyValueSorted = (() => {
    if (data.length <= 1) return true;
    for (let i = 1; i < data.length; i++) {
      if (data[i-1][valueKey] < data[i][valueKey]) {
        return false;
      }
    }
    return true;
  })();

  // Sort the data in descending order by value for better visualization
  // But only if not already sorted
  const sortedData = isAlreadyValueSorted 
    ? data 
    : [...data].sort((a, b) => b[valueKey] - a[valueKey]);

  // Calculate total for center text (if donut chart)
  const total = sortedData.reduce((sum, item) => sum + (typeof item[valueKey] === 'number' ? item[valueKey] : 0), 0)
  
  // Custom rendering for donut chart with center text
  const renderCustomizedLabel = (props: any) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, percent, name, value } = props
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5
    const x = cx + radius * Math.cos(-midAngle * Math.PI / 180)
    const y = cy + radius * Math.sin(-midAngle * Math.PI / 180)
    
    if (variant === 'labelList') {
      // More extensive label with name and value
      return (
        <text x={x} y={y} fill="hsl(var(--foreground))" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
          {`${name}: ${value}`}
        </text>
      )
    } else if (showLabels) {
      // Simple percent label
      return (
        <text x={x} y={y} fill="hsl(var(--foreground))" textAnchor="middle" dominantBaseline="central">
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      )
    }
    
    return null
  }

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={300}>
        <PieChart margin={{ top: 20, right: 20, left: 5, bottom: 5 }}>
          <Pie
            data={sortedData}
            cx="50%"
            cy="50%"
            labelLine={variant === 'labelList'}
            label={showLabels ? renderCustomizedLabel : undefined}
            outerRadius={outerRadius}
            innerRadius={innerRadius}
            fill="#8884d8"
            dataKey={valueKey}
            nameKey={nameKey}
            paddingAngle={2}
            isAnimationActive={true}
          >
            {sortedData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={colors[index % colors.length]} 
                stroke="hsl(var(--background))"
                strokeWidth={1}
              />
            ))}
          </Pie>
          
          {/* Center text for donut chart */}
          {variant === 'donut' && innerRadius > 0 && (
            <Customized
              component={({ viewBox }: any) => {
                if (!viewBox) return null;
                const { cx, cy } = viewBox;
                return (
                  <g>
                    <text
                      x={cx}
                      y={cy - 5}
                      textAnchor="middle"
                      dominantBaseline="central"
                      className="recharts-text"
                      fontSize="24"
                      fontWeight="bold"
                      fill="hsl(var(--foreground))"
                    >
                      {total.toLocaleString()}
                    </text>
                    <text
                      x={cx}
                      y={cy + 15}
                      textAnchor="middle"
                      dominantBaseline="central"
                      className="recharts-text"
                      fontSize="12"
                      fill="hsl(var(--muted-foreground))"
                    >
                      Total
                    </text>
                  </g>
                );
              }}
            />
          )}
          
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--background))",
              borderColor: "hsl(var(--border))",
              borderRadius: "6px",
              ...(tooltip?.contentStyle || {})
            }}
            labelStyle={{ 
              color: "hsl(var(--foreground))",
              ...(tooltip?.labelStyle || {})
            }}
            itemStyle={{ 
              color: "hsl(var(--foreground))",
              ...(tooltip?.itemStyle || {})
            }}
            wrapperStyle={tooltip?.wrapperStyle}
            formatter={tooltip?.formatter as any}
            content={tooltip?.content || CustomTooltip}
          />
          <Legend
            iconType="circle"
            layout="vertical"
            verticalAlign="middle"
            align="right"
            wrapperStyle={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}
          />
        </PieChart>
      </ResponsiveContainer>
      
      {/* Add suggestions list */}
      {shouldShowSuggestions && <SuggestionList suggestions={chartSuggestions} />}
    </div>
  )
}

export const RadarChartComponent: React.FC<{ config: ChartConfig; data: any[] }> = ({ config, data }) => {
  const { 
    colors = DEFAULT_COLORS,
    variant = "default", // 'default', 'dots', 'nogrid', 'multiple'
    gridType = variant === 'nogrid' ? 'none' : 'line',
    showGrid = gridType !== 'none',
    tooltip
  } = config

  // Extract suggestions if they exist in the config
  const chartSuggestions = config.suggestions || [];
  const shouldShowSuggestions = chartSuggestions.length > 0 && config.show_suggestions !== false;

  // Get all the properties that have numeric values for the first item
  const keys = Object.keys(data[0]).filter((key) => typeof data[0][key] === "number")
  const nameKey = Object.keys(data[0]).find((key) => !keys.includes(key)) || "name"

  // Check if data is already sorted alphabetically by name
  const isAlreadyNameSorted = (() => {
    if (data.length <= 1) return true;
    for (let i = 1; i < data.length; i++) {
      if (data[i-1][nameKey] > data[i][nameKey]) {
        return false;
      }
    }
    return true;
  })();

  // For radar charts, we sort by the category name for better presentation
  // But only if not already sorted
  const sortedData = isAlreadyNameSorted 
    ? data 
    : [...data].sort((a, b) => {
        if (a[nameKey] < b[nameKey]) return -1;
        if (a[nameKey] > b[nameKey]) return 1;
        return 0;
      });

  // Determine if we should show dots
  const showDots = variant === 'dots';

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={300}>
        <RadarChart cx="50%" cy="50%" outerRadius={100} data={sortedData}>
          {showGrid && gridType === 'line' && (
            <PolarGrid stroke="hsl(var(--border))" strokeDasharray="3 3" strokeOpacity={0.4} />
          )}
          {showGrid && gridType === 'circle' && (
            <PolarGrid stroke="hsl(var(--border))" strokeOpacity={0.4} gridType="circle" />
          )}
          <PolarAngleAxis dataKey={nameKey} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
          <PolarRadiusAxis
            angle={30}
            domain={[0, "auto"]}
            tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
          />
          {/* For multiple variant display each key as a separate radar */}
          {variant === 'multiple' ? (
            keys.map((key, index) => (
              <Radar
                key={key}
                name={key}
                dataKey={key}
                stroke={colors[index % colors.length]}
                fill={colors[index % colors.length]}
                fillOpacity={0.3}
                dot={showDots ? { fill: colors[index % colors.length], r: 4 } : false}
                isAnimationActive={true}
              />
            ))
          ) : (
            // Default behavior - show the first numeric column
            <Radar
              name={keys[0]}
              dataKey={keys[0]}
              stroke={colors[0]}
              fill={colors[0]}
              fillOpacity={0.3}
              dot={showDots ? { fill: colors[0], r: 4 } : false}
              isAnimationActive={true}
            />
          )}
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--background))",
              borderColor: "hsl(var(--border))",
              borderRadius: "6px",
            }}
            labelStyle={{ color: "hsl(var(--foreground))" }}
            itemStyle={{ color: "hsl(var(--foreground))" }}
            content={tooltip?.content || CustomTooltip}
          />
          <Legend iconType="circle" wrapperStyle={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }} />
        </RadarChart>
      </ResponsiveContainer>
      
      {/* Add suggestions list */}
      {shouldShowSuggestions && <SuggestionList suggestions={chartSuggestions} />}
    </div>
  )
}

export const RadialBarChartComponent: React.FC<{ config: ChartConfig; data: any[] }> = ({ config, data }) => {
  const { 
    colors = DEFAULT_COLORS,
    variant = "default", // 'default', 'label', 'text'
    innerRadius = 20,
    outerRadius = 120,
    showLabels = variant === 'label',
    tooltip
  } = config

  // Extract suggestions if they exist in the config
  const chartSuggestions = config.suggestions || [];
  const shouldShowSuggestions = chartSuggestions.length > 0 && config.show_suggestions !== false;

  const valueKey = Object.keys(data[0]).find((key) => typeof data[0][key] === "number") || "value"
  const nameKey = Object.keys(data[0]).find((key) => key !== valueKey) || "name"
  
  // Check if data is already sorted in descending order by value
  const isAlreadyValueSorted = (() => {
    if (data.length <= 1) return true;
    for (let i = 1; i < data.length; i++) {
      if (data[i-1][valueKey] < data[i][valueKey]) {
        return false;
      }
    }
    return true;
  })();
  
  // Sort by value in descending order for better visualization
  // But only if not already sorted
  const sortedData = isAlreadyValueSorted 
    ? data 
    : [...data].sort((a, b) => b[valueKey] - a[valueKey]);
  
  // Calculate total for center text
  const total = sortedData.reduce((sum, item) => sum + (typeof item[valueKey] === 'number' ? item[valueKey] : 0), 0)

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={300}>
        <RadialBarChart
          cx="50%"
          cy="50%"
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          barSize={12}
          data={sortedData}
          startAngle={180}
          endAngle={0}
        >
          <RadialBar
            label={showLabels ? {
              position: "insideStart",
              fill: "hsl(var(--muted-foreground))",
              fontSize: 11,
            } : false}
            background={{ fill: "hsl(var(--muted))", opacity: 0.2 }}
            dataKey={valueKey}
          >
            {sortedData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </RadialBar>
          
          {/* Center text */}
          {variant === 'text' && (
            <Customized
              component={({ viewBox }: any) => {
                if (!viewBox) return null;
                const { cx, cy } = viewBox;
                return (
                  <g>
                    <text
                      x={cx}
                      y={cy - 5}
                      textAnchor="middle"
                      dominantBaseline="central"
                      className="recharts-text"
                      fontSize="24"
                      fontWeight="bold"
                      fill="hsl(var(--foreground))"
                    >
                      {total.toLocaleString()}
                    </text>
                    <text
                      x={cx}
                      y={cy + 15}
                      textAnchor="middle"
                      dominantBaseline="central"
                      className="recharts-text"
                      fontSize="12"
                      fill="hsl(var(--muted-foreground))"
                    >
                      Total
                    </text>
                  </g>
                );
              }}
            />
          )}
          
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--background))",
              borderColor: "hsl(var(--border))",
              borderRadius: "6px",
            }}
            labelStyle={{ color: "hsl(var(--foreground))" }}
            itemStyle={{ color: "hsl(var(--foreground))" }}
            content={tooltip?.content || CustomTooltip}
          />
          <Legend
            iconType="circle"
            layout="vertical"
            verticalAlign="middle"
            align="right"
            wrapperStyle={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}
            formatter={(value: string | number) => {
              // Force to string and check
              const valueStr = String(value);
              if (typeof valueStr === 'string' && valueStr.endsWith('_numeric')) {
                return valueStr.replace('_numeric', '');
              }
              return value;
            }}
          />
        </RadialBarChart>
      </ResponsiveContainer>
      
      {/* Add suggestions list */}
      {shouldShowSuggestions && <SuggestionList suggestions={chartSuggestions} />}
    </div>
  )
}

export const TreeMapChartComponent: React.FC<{ config: ChartConfig; data: any[] }> = ({ config, data }) => {
  const { 
    colors = DEFAULT_COLORS,
    variant = "default",
    dataKey,
    xAxisKey,
    tooltip
  } = config
  
  // Extract suggestions if they exist in the config
  const chartSuggestions = config.suggestions || [];
  const shouldShowSuggestions = chartSuggestions.length > 0 && config.show_suggestions !== false;
  
  // Handle empty or invalid data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
          No data available for TreeMap visualization
        </div>
      </ResponsiveContainer>
    );
  }
  
  // Process the data for hierarchical visualization
  const processData = (): any[] => {
    try {
      // If data is already in the format with 'children' arrays, use it as is
      if (data.length > 0 && data[0].children) {
        return data;
      }
      
      // Find all keys in first data item
      const firstItem = data[0] || {};
      const allKeys = Object.keys(firstItem);
      
      // Determine key to use for naming (category field)
      const nameKey = xAxisKey || 
        allKeys.find(key => 
          key === 'name' || 
          key === 'category' || 
          key === 'Level' || 
          key === 'level'
        ) || 
        allKeys.find(key => typeof firstItem[key] === 'string');
      
      // Determine key to use for sizing
      const sizeKey = dataKey || 
        allKeys.find(key => 
          key === 'value' || 
          key === 'size' || 
          key === 'Total' || 
          key === 'total'
        ) || 
        allKeys.find(key => 
          key !== nameKey && 
          typeof firstItem[key] === 'number'
        );
      
      if (!sizeKey || !nameKey) {
        // Fallback: Create a simple tree with equal-sized nodes
        return [{
          name: "Data",
          children: data.map((item, index) => ({
            name: `Item ${index + 1}`,
            size: 1
          }))
        }];
      }
      
      // Find all numeric keys for potential child nodes (except the main size key)
      const numericKeys = allKeys.filter(key => 
        typeof firstItem[key] === 'number' && 
        key !== sizeKey
      );
      
      // Create root node with categories
      const categories = data.map((item, index) => {
        if (!item) return null;
        
        // Get the category name from the item
        const categoryName = String(item[nameKey] || `Item ${index + 1}`);
        const totalValue = typeof item[sizeKey] === 'number' ? item[sizeKey] : 1;
        
        // Skip items with zero or negative size
        if (totalValue <= 0) return null;
        
        // Create children for each component if there are numeric fields
        const components = numericKeys
          .filter(key => typeof item[key] === 'number' && item[key] > 0)
          .map(key => {
            const componentName = String(key).replace(/_/g, ' ');
            return {
              name: componentName,
              size: item[key],
              category: categoryName // Store the parent category name
            };
          });
        
        return {
          name: categoryName,
          size: totalValue,
          children: components.length > 0 ? components : undefined
        };
      }).filter(Boolean);
      
      // If no valid categories were found, create a simple fallback
      if (categories.length === 0) {
        return [{
          name: "Data",
          children: data.map((_, index) => ({
            name: `Item ${index + 1}`,
            size: 1
          }))
        }];
      }
      
      return [{
        name: "All Data",
        children: categories
      }];
    } catch (error) {
      console.error("Error processing TreeMap data:", error);
      // Fallback for any errors
      return [{
        name: "Error",
        children: [{ name: "Unable to process data", size: 1 }]
      }];
    }
  };
  
  // Process the data
  const processedData = processData();
  
  // Customize the content of tooltip
  const renderTooltip = (props: any) => {
    try {
      if (!props.active || !props.payload || props.payload.length === 0) {
        return null;
      }
      
      const data = props.payload[0].payload;
      if (!data) return null;
      
      const categoryName = data.category || (data.depth > 0 && data.parent ? data.parent.name : null);
      const displayValue = data.value || data.size;
      
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: "hsl(var(--background))",
          border: "1px solid hsl(var(--border))",
          borderRadius: "6px",
          padding: "8px 12px",
        }}>
          <p className="label" style={{ color: "hsl(var(--foreground))", margin: "0 0 4px 0" }}>
            <strong>{data.name || 'Unnamed'}</strong>
          </p>
          {categoryName && categoryName !== "All Data" && data.depth > 1 && (
            <p style={{ color: "hsl(var(--muted-foreground))", margin: "0 0 4px 0", fontSize: "0.85rem" }}>
              {categoryName}
            </p>
          )}
          {typeof displayValue === 'number' && (
            <p style={{ color: "hsl(var(--foreground))", margin: 0 }}>
              {displayValue.toLocaleString()}
            </p>
          )}
        </div>
      );
    } catch (error) {
      console.error("Error rendering TreeMap tooltip:", error);
      return null;
    }
  };
  
  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={400}>
        <Treemap
          data={processedData}
          dataKey="size"
          stroke="hsl(var(--background))"
          fill={colors[0]}
          aspectRatio={4/3}
          content={
            <CustomizedTreemapContent
              colors={colors}
              variant={variant}
            />
          }
        >
          <Tooltip content={tooltip?.content || renderTooltip} />
        </Treemap>
      </ResponsiveContainer>
      
      {/* Use the SuggestionList component instead of inline code */}
      {shouldShowSuggestions && <SuggestionList suggestions={chartSuggestions} />}
    </div>
  );
};

// Custom treemap cell rendering
const CustomizedTreemapContent: React.FC<any> = (props) => {
  try {
    const { 
      depth = 0, 
      x = 0, 
      y = 0, 
      width = 0, 
      height = 0, 
      index = 0, 
      colors = DEFAULT_COLORS, 
      name, 
      value, 
      variant,
      size
    } = props;
    
    // For nested treemaps with different depths
    const isNested = variant === 'nest';
    
    // Calculate opacity based on depth - lighter and more transparent
    const opacity = isNested ? 
      Math.max(0.4, 1 - 0.15 * depth) : 
      depth === 0 ? 0.3 :   // Root node - very transparent
      depth === 1 ? 0.7 :   // Category node - moderate opacity
      0.65;                 // Component node - similar to bar charts
    
    // Determine color index based on hierarchical level
    let colorIndex = 0;
    
    if (depth === 0) {
      // Root node - use neutral color
      colorIndex = 7; // Gray
    } else if (depth === 1) {
      // Category nodes - each gets its own color
      colorIndex = index % colors.length;
    } else {
      // Component nodes - use parent color with slight variation
      const parentIndex = props.parent?.index || 0;
      colorIndex = (parentIndex + (index % 2)) % colors.length;
    }
    
    // Get the fill color
    const fill = colors[colorIndex % colors.length];
    
    // Don't render if width or height is 0
    if (width <= 0 || height <= 0) return null;
    
    // For tiny rectangles, render without text
    if (width < 10 || height < 10) {
      return (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
          fillOpacity={opacity * 0.5}
          stroke="hsl(var(--background))"
          strokeWidth={0.5}
          rx={1}
          ry={1}
        />
      );
    }
    
    // Text display threshold - only show text if enough space
    const showTitle = width > 50 && height > 25 && name;
    const showValue = width > 70 && height > 40 && (value || size);
    
    // Calculate text color for better contrast
    const getContrastColor = (colorIndex: number): string => {
      // Dark colors need white text
      const darkColors = [0, 1, 4, 6]; // Blue, indigo, green, gray
      if (darkColors.includes(colorIndex % colors.length)) {
        return "rgba(255, 255, 255, 0.95)";
      }
      // Light colors need dark text
      return "rgba(0, 0, 0, 0.85)";
    };
    
    const textColor = getContrastColor(colorIndex);
    
    // Get name length safely
    const nameLength = name && typeof name === 'string' ? name.length : 8;
    const displayName = typeof name === 'string' ? name.replace(/_/g, ' ') : '';
    
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
          fillOpacity={opacity}
          stroke="hsl(var(--background))"
          strokeWidth={depth < 2 ? 1 : 0.5}
          rx={2}
          ry={2}
        />
        {showTitle && (
          <text
            x={x + width / 2}
            y={y + height / 2 - (showValue ? 7 : 0)}
            textAnchor="middle"
            fill={textColor}
            fontSize={Math.min(11, width / (nameLength > 10 ? 11 : 7))}
            fontWeight={depth < 2 ? "bold" : "normal"}
          >
            {displayName}
          </text>
        )}
        {showValue && (
          <text
            x={x + width / 2}
            y={y + height / 2 + 14}
            textAnchor="middle"
            fill={textColor}
            fontSize={10}
          >
            {typeof value === 'number' 
              ? value.toLocaleString() 
              : typeof size === 'number' 
                ? size.toLocaleString() 
                : ''}
          </text>
        )}
      </g>
    );
  } catch (error) {
    console.error("Error rendering TreeMap cell:", error);
    return null;
  }
};

export const BarChartWithBrushComponent: React.FC<{ config: ChartConfig; data: any[] }> = ({ config, data }) => {
  const { 
    xAxisKey = "name", 
    yAxisKeys = ["value"], 
    colors = DEFAULT_COLORS,
    variant = "default", // 'default', 'horizontal'
    barSize = 20,
    showLabels = false,
    showGrid = false,
    showBrush = true,
    tooltip
  } = config

  // Extract suggestions if they exist in the config
  const chartSuggestions = config.suggestions || [];
  const shouldShowSuggestions = chartSuggestions.length > 0 && config.show_suggestions !== false;

  // Calculate the height of the chart based on data length for better readability
  const chartHeight = Math.max(300, Math.min(600, data.length * 15));
  
  // Sort data for sequential data types if needed
  const sortedData = sortDataIfNeeded(data, xAxisKey);

  // Fix for yAxisKeys if it includes xAxisKey
  const validYAxisKeys = yAxisKeys.filter(key => key !== xAxisKey);

  // For negative values
  const hasNegativeValues = sortedData.some(item => 
    validYAxisKeys.some(key => typeof item[key] === 'number' && item[key] < 0)
  );

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart 
          data={sortedData} 
          margin={{ top: 20, right: 20, left: 20, bottom: 60 }}
        >
          {showGrid && <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />}
          <XAxis
            dataKey={xAxisKey}
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tick={{ fill: "hsl(var(--muted-foreground))" }}
            tickMargin={8}
            fontSize={12}
            height={50}
          />
          <YAxis
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tick={{ fill: "hsl(var(--muted-foreground))" }}
            fontSize={12}
            width={60}
          />
          <Tooltip
            cursor={{ fill: "hsla(var(--muted), 0.1)" }}
            contentStyle={{
              backgroundColor: "hsl(var(--background))",
              borderColor: "hsl(var(--border))",
              borderRadius: "6px",
            }}
            labelStyle={{ color: "hsl(var(--foreground))" }}
            itemStyle={{ color: "hsl(var(--foreground))" }}
            content={tooltip?.content || CustomTooltip}
          />
          <Legend 
            iconType="circle" 
            verticalAlign="top"
            wrapperStyle={{ fontSize: 12, color: "hsl(var(--muted-foreground))", paddingBottom: 10 }} 
          />
          
          {/* Add reference line at y=0 for charts with negative values */}
          {hasNegativeValues && (
            <ReferenceLine y={0} stroke="hsl(var(--border))" strokeWidth={1.5} strokeOpacity={0.8} />
          )}
          
          {/* Add brush for time series data */}
          {showBrush && (
            <Brush 
              dataKey={xAxisKey} 
              height={30} 
              stroke={colors[0]} 
              fill="hsla(var(--muted), 0.1)"
              startIndex={0}
              endIndex={Math.min(9, data.length - 1)}
              y={chartHeight - 60}
              travellerWidth={10}
            />
          )}
          
          {validYAxisKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              name={key}
              fill={colors[index % colors.length]}
              stroke={colors[index % colors.length]}
              strokeWidth={1}
              radius={[4, 4, 0, 0]}
              barSize={barSize}
              isAnimationActive={true}
            >
              {hasNegativeValues && (
                sortedData.map((entry, i) => (
                  <Cell
                    key={`cell-${i}`}
                    fill={entry[key] >= 0 ? colors[index % colors.length] : colors[(index + 1) % colors.length]}
                  />
                ))
              )}
              {showLabels && (
                <LabelList 
                  dataKey={key} 
                  position="top" 
                  fill="hsl(var(--foreground))" 
                  fontSize={12} 
                />
              )}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
      
      {/* Add suggestions list */}
      {shouldShowSuggestions && <SuggestionList suggestions={chartSuggestions} />}
    </div>
  )
}

export const ScatterChartComponent: React.FC<{ config: ChartConfig; data: any[] }> = ({ config, data }) => {
  const { 
    xAxisKey = "x", 
    yAxisKeys = ["y"],
    zAxisKey,
    colors = DEFAULT_COLORS,
    variant = "default", // 'default', 'bubble', 'multi', 'jitter', 'advanced'
    showGrid = false,
    tooltip
  } = config

  // Extract suggestions if they exist in the config
  const chartSuggestions = config.suggestions || [];
  const shouldShowSuggestions = chartSuggestions.length > 0 && config.show_suggestions !== false;

  // Apply date transformation to ensure date strings are converted to numeric values
  const { transformedData, numericKeys, hasDateValues } = transformDateValues(data, yAxisKeys, xAxisKey);

  // For bubble charts, we need a z-axis key for the bubble size
  const bubbleZAxisKey = zAxisKey || 
    (variant === 'bubble' ? 
      Object.keys(data[0]).find(key => 
        key !== xAxisKey && 
        !yAxisKeys.includes(key) && 
        typeof data[0][key] === 'number'
      ) || 'z' 
    : undefined);

  // Smart X-axis type detection - we need to check ALL values, not just the first
  const determineAxisType = (key: string, dataArray: any[]): "category" | "number" => {
    // If all values are numeric, use number type
    if (dataArray.every(item => typeof item[key] === 'number')) {
      return "number";
    }
    
    // If any value is a string or the key suggests categorical data, use category
    if (dataArray.some(item => typeof item[key] === 'string') || 
        ['category', 'group', 'type', 'hour', 'day', 'month', 'year', 'quarter', 'week'].includes(key.toLowerCase())) {
      return "category";
    }
    
    // Default to category for safety
    return "category";
  };

  // Process data to support multiple series in different formats
  const processData = () => {
    // Return null if no data
    if (!transformedData || transformedData.length === 0) return { dataGroups: {}, seriesNames: [] };
    
    // Case 1: Data has explicit series marker (like a 'series' or 'group' field)
    const possibleSeriesKeys = Object.keys(transformedData[0]).filter(key => 
      typeof transformedData[0][key] === 'string' && 
      key !== xAxisKey && 
      !yAxisKeys.includes(key) &&
      ['series', 'group', 'category', 'type', 'name'].some(marker => key.toLowerCase().includes(marker))
    );
    
    if (possibleSeriesKeys.length > 0) {
      const seriesKey = possibleSeriesKeys[0];
      const seriesNames = [...new Set(transformedData.map(item => item[seriesKey]))];
      
      // Group by series key
      const dataGroups = seriesNames.reduce((acc, series) => {
        acc[series] = transformedData.filter(item => item[seriesKey] === series);
        return acc;
      }, {} as Record<string, any[]>);
      
      return { dataGroups, seriesNames, seriesKey };
    }
    
    // Case 2: Multiple yAxisKeys suggest multi-series data
    if (numericKeys.length > 1) {
      // Transform data to have one series per y-axis key
      const dataGroups: Record<string, any[]> = {};
      
      numericKeys.forEach(yKey => {
        dataGroups[yKey] = transformedData.map(item => ({
          [xAxisKey]: item[xAxisKey],
          y: item[yKey],
          // Preserve z-axis data if available
          ...(bubbleZAxisKey && { [bubbleZAxisKey]: item[bubbleZAxisKey] })
        }));
      });
      
      return { dataGroups, seriesNames: numericKeys };
    }
    
    // Case 3: Data structure suggests it's already pre-grouped for multiple series
    // Check if there are exactly 2 numeric keys and they have very different ranges
    const allNumericKeys = Object.keys(transformedData[0]).filter(key => 
      typeof transformedData[0][key] === 'number' && key !== xAxisKey
    );
    
    if (allNumericKeys.length === 2) {
      // Get ranges for both numeric properties
      const ranges = allNumericKeys.map(key => {
        const values = transformedData.map(item => item[key]);
        return {
          key,
          min: Math.min(...values),
          max: Math.max(...values)
        };
      });
      
      // If ranges are very different, these are likely different metrics
      if (Math.abs(ranges[0].max - ranges[1].max) > Math.max(ranges[0].max, ranges[1].max) * 0.5) {
        const dataGroups: Record<string, any[]> = {};
        
        allNumericKeys.forEach(key => {
          dataGroups[key] = transformedData.map(item => ({
            [xAxisKey]: item[xAxisKey],
            y: item[key],
            // Preserve z-axis data if available
            ...(bubbleZAxisKey && { [bubbleZAxisKey]: item[bubbleZAxisKey] })
          }));
        });
        
        return { dataGroups, seriesNames: allNumericKeys };
      }
    }
    
    // Case 4: No clear series indication, treat as a single series
    return { 
      dataGroups: { "default": transformedData }, 
      seriesNames: ["default"]
    };
  };
  
  // Process the data
  const { dataGroups, seriesNames, seriesKey } = processData();
  
  // Determine appropriate shape based on variant and series count
  const getScatterShape = (seriesIndex: number): "circle" | "triangle" | "square" | "star" | "diamond" | "cross" => {
    if (variant === 'advanced') {
      // Use different shapes for visibility
      const shapes: Array<"circle" | "triangle" | "square" | "star" | "diamond" | "cross"> = [
        "circle", "triangle", "square", "star", "diamond", "cross"
      ];
      return shapes[seriesIndex % shapes.length];
    }
    return "circle";
  };
  
  // Determine X-axis type
  const xAxisType = determineAxisType(xAxisKey, transformedData);
  
  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={300}>
        <ScatterChart
          margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
        >
          {showGrid && <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />}
          <XAxis 
            type={xAxisType}
            dataKey={xAxisKey}
            name={xAxisKey}
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tick={{ fill: "hsl(var(--muted-foreground))" }}
            tickMargin={8}
            fontSize={12}
          />
          <YAxis 
            type="number" 
            dataKey={seriesNames.length > 1 ? "y" : numericKeys[0]}
            name={seriesNames.length > 1 ? "Value" : numericKeys[0]}
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tick={{ fill: "hsl(var(--muted-foreground))" }}
            fontSize={12}
          />
          
          {variant === 'bubble' && bubbleZAxisKey && (
            <ZAxis 
              type="number" 
              dataKey={bubbleZAxisKey} 
              range={[40, 160]} 
              name={bubbleZAxisKey} 
            />
          )}
          
          <Tooltip 
            cursor={{ strokeDasharray: '3 3' }}
            contentStyle={{
              backgroundColor: "hsl(var(--background))",
              borderColor: "hsl(var(--border))",
              borderRadius: "6px",
            }}
            labelStyle={{ color: "hsl(var(--foreground))" }}
            itemStyle={{ color: "hsl(var(--foreground))" }}
            content={tooltip?.content || CustomTooltip}
          />
          
          <Legend 
            iconType="circle" 
            wrapperStyle={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}
            formatter={(value: string | number) => {
              // Force to string and check
              const valueStr = String(value);
              if (valueStr.endsWith('_numeric')) {
                return valueStr.replace('_numeric', '');
              }
              return value;
            }}
          />
          
          {/* Render each data series */}
          {seriesNames.map((series, index) => {
            // Skip empty series
            if (!dataGroups[series] || dataGroups[series].length === 0) return null;
            
            // Generate display name - if series is a generated yAxisKey name, use that directly
            const displayName = series === "default" ? (
              numericKeys[0] || "Values"
            ) : (
              // If it's from yAxisKeys directly, use as is
              numericKeys.includes(series) ? series : 
              // If it was a series key value, use that
              series
            );
            
            // Format display name to remove _numeric suffix if present
            const formattedName = typeof displayName === 'string' && displayName.endsWith('_numeric') ? 
              displayName.replace('_numeric', '') : displayName;
            
            return (
              <Scatter 
                key={series}
                name={formattedName}
                data={dataGroups[series]} 
                fill={colors[index % colors.length]}
                shape={getScatterShape(index)}
              />
            );
          })}
        </ScatterChart>
      </ResponsiveContainer>
      
      {/* Add suggestions list */}
      {shouldShowSuggestions && <SuggestionList suggestions={chartSuggestions} />}
    </div>
  )
}

export const AdvancedScatterChartComponent: React.FC<{ config: ChartConfig; data: any[] }> = ({ config, data }) => {
  const { 
    xAxisKey,
    yAxisKeys = [],
    zAxisKey,
    colors = DEFAULT_COLORS,
    variant = "multiBubble",
    showGrid = false,
    seriesKey,
    tooltip
  } = config;

  // Extract suggestions if they exist in the config
  const chartSuggestions = config.suggestions || [];
  const shouldShowSuggestions = chartSuggestions.length > 0 && config.show_suggestions !== false;

  // Check if we have calendar layout data (day of week series)
  const isCalendarStyle = variant === 'calendar' ||
    (data.length > 0 && 
     data.some(item => item.day || item.dayOfWeek || item.weekday));

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-muted-foreground">
        No data available for advanced scatter chart
      </div>
    );
  }

  // Auto-detect fields if not explicitly provided
  const firstItem = data[0] || {};
  const allKeys = Object.keys(firstItem);
  
  // Auto-detect x-axis key - prioritize time fields for scatter plots
  const xKey = xAxisKey || 
    allKeys.find(key => ['year', 'date', 'time', 'month', 'quarter'].some(t => key.toLowerCase().includes(t))) ||
    allKeys[0];
  
  // Auto-detect y-axis key (must be numeric)
  const yKey = yAxisKeys.length > 0 ? yAxisKeys[0] : 
    allKeys.find(key => 
      key !== xKey && 
      typeof firstItem[key] === 'number' &&
      ['total', 'value', 'amount', 'salary', 'compensation'].some(t => key.toLowerCase().includes(t))
    ) || 
    allKeys.find(key => 
      key !== xKey && 
      typeof firstItem[key] === 'number'
    );
    
  if (!yKey) {
    return (
      <div className="flex items-center justify-center h-40 text-muted-foreground">
        No numeric data available for advanced scatter chart
      </div>
    );
  }
  
  // Find the series key for grouping data
  // First try the explicitly provided seriesKey, then look for common grouping fields
  const groupingKey = seriesKey || 
    allKeys.find(key => 
      key !== xKey && 
      key !== yKey && 
      typeof firstItem[key] === 'string' &&
      ['level', 'category', 'group', 'type', 'series'].some(t => key.toLowerCase().includes(t))
    );
  
  // Create a consistent series grouping based on the grouping key
  const seriesGroups: Record<string, any[]> = {};
  
  if (groupingKey) {
    // Get unique series values from the grouping field
    const uniqueSeriesValues = [...new Set(data.map(item => String(item[groupingKey])))];
    
    // Group data by series value
    uniqueSeriesValues.forEach(seriesValue => {
      seriesGroups[seriesValue] = data.filter(item => 
        String(item[groupingKey]) === seriesValue
      );
    });
  } else {
    // No group field found, try other ways to group the data
    
    // Try to group by any categorical field if not used as xKey or yKey
    const categoricalKeys = allKeys.filter(key => 
      key !== xKey &&
      key !== yKey && 
      typeof firstItem[key] === 'string'
    );
    
    if (categoricalKeys.length > 0) {
      // Use the first categorical field for grouping
      const altGroupKey = categoricalKeys[0];
      const uniqueValues = [...new Set(data.map(item => String(item[altGroupKey])))];
      
      uniqueValues.forEach(value => {
        seriesGroups[value] = data.filter(item => String(item[altGroupKey]) === value);
      });
    } else {
      // No grouping field, use data as one series
      seriesGroups['Data'] = data;
    }
  }
  
  // Get domain for bubble size if needed
  const bubbleSizeKey = zAxisKey || yKey;
  
  // Parse domain for bubble sizes
  const parseDomain = () => {
    if (!data || data.length === 0 || !bubbleSizeKey) return [0, 100];
    
    const values: number[] = [];
    
    for (const entry of data) {
      if (entry && typeof entry[bubbleSizeKey] === 'number' && !isNaN(entry[bubbleSizeKey])) {
        values.push(entry[bubbleSizeKey]);
      }
    }
    
    if (values.length === 0) return [0, 100];
    
    return [
      Math.min(...values, 0),
      Math.max(...values)
    ];
  };
  
  const domain = parseDomain();
  
  // Generate shapes for each series
  const getSeriesShape = (index: number): "circle" | "triangle" | "square" | "star" | "wye" | "cross" => {
    const shapes: Array<"circle" | "triangle" | "square" | "star" | "wye" | "cross"> = [
      "circle", "triangle", "square", "star", "wye", "cross"
    ];
    return shapes[index % shapes.length];
  };
  
  // Determine x-axis type based on data
  const xAxisType = (() => {
    // Check if we're using a time-based x-axis
    const isTimeField = ['year', 'date', 'month', 'quarter', 'day'].some(t => 
      xKey.toLowerCase().includes(t)
    );
    
    // For year fields, keep as number if numeric
    if (xKey.toLowerCase().includes('year') && typeof firstItem[xKey] === 'number') {
      return 'number';
    }
    
    // If all values are numeric, use number; otherwise use category
    if (data.every(item => typeof item[xKey] === 'number')) {
      return 'number';
    }
    
    return 'category';
  })();
  
  // Custom tooltip with indicators for AdvancedScatterChart
  const CustomAdvancedTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    const item = payload[0];
    const hasSeries = !!seriesKey && item.payload[seriesKey] !== undefined;

      return (
      <div className="bg-popover/95 backdrop-blur-sm shadow-md rounded-md p-2 border border-border text-xs">
        {hasSeries && (
          <div className="flex items-center gap-2 mb-1.5 pb-1.5 border-b border-border/30">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: item.color }}
            />
            <span className="font-medium">{item.payload[seriesKey!]}</span>
          </div>
        )}
        
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-primary/50 rounded-sm flex-shrink-0"></span>
            <span className="text-muted-foreground">{xKey}:</span>
            <span className="font-medium ml-auto">
              {typeof item.payload[xKey] === 'number' 
                ? item.payload[xKey].toLocaleString() 
                : item.payload[xKey]}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-primary/80 rounded-sm flex-shrink-0"></span>
            <span className="text-muted-foreground">{yKey}:</span>
            <span className="font-medium ml-auto">
              {typeof item.payload[yKey] === 'number' 
                ? item.payload[yKey].toLocaleString() 
                : item.payload[yKey]}
            </span>
          </div>
          
          {zAxisKey && item.payload[zAxisKey] !== undefined && (
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-accent/80 rounded-full flex-shrink-0"></span>
              <span className="text-muted-foreground">{zAxisKey}:</span>
              <span className="font-medium ml-auto">
                {typeof item.payload[zAxisKey] === 'number' 
                  ? item.payload[zAxisKey].toLocaleString() 
                  : item.payload[zAxisKey]}
              </span>
            </div>
          )}
        </div>
        </div>
      );
  };
  
  // Rest of the existing implementation...
  
  // Use the updated CustomAdvancedTooltip in the chart's Tooltip component
  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={400}>
        <ScatterChart
          margin={{
            top: 20,
            right: 20,
            bottom: 20,
            left: 20,
          }}
        >
          {showGrid && <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />}
          <XAxis 
            type={xAxisType as any}
            dataKey={xKey}
            name={xKey}
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tick={{ fill: "hsl(var(--muted-foreground))" }}
          />
          <YAxis 
            type="number" 
            dataKey={yKey}
            name={yKey}
            tickLine={false}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tick={{ fill: "hsl(var(--muted-foreground))" }}
          />
          <ZAxis 
            type="number" 
            dataKey={bubbleSizeKey} 
            range={[60, 400]} 
            domain={domain} 
            name={bubbleSizeKey} 
          />
          <Tooltip 
            cursor={{ strokeDasharray: '3 3' }}
            contentStyle={{
              backgroundColor: "hsl(var(--background))",
              borderColor: "hsl(var(--border))",
              borderRadius: "6px",
            }}
            content={tooltip?.content || CustomAdvancedTooltip}
          />
          <Legend 
            iconType="circle" 
            wrapperStyle={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}
            formatter={(value, entry) => {
              // Only show top 4 items in legend
              // Find which series this entry represents by matching with seriesGroups
              const seriesEntries = Object.entries(seriesGroups);
              const index = seriesEntries.findIndex(([s]) => s === value);
              
              if (index >= 4) return null;
              
              // For the 4th legend item, show "Others" if more than 4 series
              if (seriesEntries.length > 4 && index === 3) {
                return `Others (${seriesEntries.length - 3} more)`;
              }
              return value;
            }}
          />
          
          {Object.entries(seriesGroups).map(([series, groupData], index) => {
            // Determine visibility in legend - all data is shown but only first 4 in legend
            const legendType = index < 4 ? "circle" : "none";
            
            return (
              <Scatter 
                key={series}
                name={series}
                data={groupData} 
                fill={colors[index % colors.length]}
                shape={getSeriesShape(index)}
                legendType={legendType}
              />
            );
          })}
        </ScatterChart>
      </ResponsiveContainer>
      
      {/* Add suggestions list */}
      {shouldShowSuggestions && <SuggestionList suggestions={chartSuggestions} />}
    </div>
  );
};

export const BubblePlotComponent: React.FC<{ config: ChartConfig; data: any[] }> = ({ config, data }) => {
  const { 
    colors = DEFAULT_COLORS,
    variant = "calendar",
    xAxisKey,
    yAxisKeys = [], 
    zAxisKey,
    seriesKey,
    tooltip
  } = config

  // Extract suggestions if they exist in the config
  const chartSuggestions = config.suggestions || [];
  const shouldShowSuggestions = chartSuggestions.length > 0 && config.show_suggestions !== false;

  // If data is provided as is, use it directly
  const chartData = data || [];
  
  if (!chartData.length) {
    return (
      <div className="flex items-center justify-center h-40 text-muted-foreground">
        No data available for bubble plot
      </div>
    );
  }

  // Auto-detect keys if not specified
  const firstItem = chartData[0] || {};
  const allKeys = Object.keys(firstItem);
  
  // Dynamically determine x-axis key if not provided - prioritize time fields
  const xKey = xAxisKey || 
    allKeys.find(key => ['year', 'date', 'time', 'month', 'quarter'].some(t => key.toLowerCase().includes(t))) ||
    allKeys[0];
  
  // Determine value field (numeric field for y-axis) - prioritize fields like 'total', 'value', etc.
  const valueKey = yAxisKeys.length > 0 ? yAxisKeys[0] : 
    allKeys.find(key => 
      key !== xKey && 
      typeof firstItem[key] === 'number' &&
      ['total', 'value', 'amount', 'salary', 'compensation'].some(t => key.toLowerCase().includes(t))
    ) || 
    allKeys.find(key => 
      key !== xKey && 
      typeof firstItem[key] === 'number'
    );
  
  if (!valueKey) {
    return (
      <div className="flex items-center justify-center h-40 text-muted-foreground">
        No numeric data available for bubble plot
      </div>
    );
  }
    
  // Find the series key for grouping data - first try explicitly provided one, then look for common patterns
  const groupingKey = seriesKey || 
    allKeys.find(key => 
      key !== xKey && 
      key !== valueKey && 
      typeof firstItem[key] === 'string' &&
      ['level', 'category', 'group', 'type', 'series'].some(t => key.toLowerCase().includes(t))
    );
  
  // Determine bubble size key - if specified in config, use that, otherwise use valueKey
  const bubbleSizeKey = zAxisKey || valueKey;
  
  // Group data by series if a series key is available
  const seriesGroups: Record<string, any[]> = {};
  
  if (groupingKey) {
    // Get unique series values that aren't null/undefined
    const seriesValues = [...new Set(chartData
      .map(item => item[groupingKey])
      .filter(val => val !== undefined && val !== null)
      .map(String)
    )];
    
    // Group data by each series value
    seriesValues.forEach(series => {
      seriesGroups[series] = chartData.filter(item => 
        item[groupingKey] !== undefined && 
        String(item[groupingKey]) === series
      );
    });
  } else {
    // Try to find another categorical field to group by
    const categoryFields = allKeys.filter(key => 
      key !== xKey &&
      key !== valueKey &&
      typeof firstItem[key] === 'string'
    );
    
    if (categoryFields.length > 0) {
      const altGroupKey = categoryFields[0];
      const categories = [...new Set(chartData.map(item => String(item[altGroupKey])))];
      
      categories.forEach(category => {
        seriesGroups[category] = chartData.filter(item => String(item[altGroupKey]) === category);
      });
    } else {
      // If no series key, treat all data as one series
      seriesGroups['All'] = chartData;
    }
  }
  
  // Parse domain for bubble sizes - with safety check for null/undefined values
  const parseDomain = () => {
    if (!chartData || chartData.length === 0 || !bubbleSizeKey) return [0, 100];
    
    const values: number[] = [];
    
    for (const entry of chartData) {
      if (entry && typeof entry[bubbleSizeKey] === 'number' && !isNaN(entry[bubbleSizeKey])) {
        values.push(entry[bubbleSizeKey]);
      }
    }
    
    if (values.length === 0) return [0, 100];
    
    return [
      Math.max(Math.min(...values, 0), 0), // Ensure minimum is at least 0 for bubble sizes
      Math.max(...values)
    ];
  };

  // Custom tooltip component
  const renderTooltip = (props: any) => {
    const { active, payload } = props;

    if (active && payload?.[0]?.payload) {
      const data = payload[0].payload;

      return (
        <div
          style={{
            backgroundColor: "hsl(var(--background))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "6px",
            margin: 0,
            padding: 10,
          }}
        >
          {groupingKey && data[groupingKey] !== undefined && (
            <p className="font-medium">{groupingKey}: {String(data[groupingKey])}</p>
          )}
          {xKey && data[xKey] !== undefined && (
            <p className="font-medium">{xKey}: {String(data[xKey])}</p>
          )}
          {valueKey && data[valueKey] !== undefined && (
            <p className="text-muted-foreground">
              {valueKey}: {data[valueKey]}
            </p>
          )}
          {bubbleSizeKey && bubbleSizeKey !== valueKey && data[bubbleSizeKey] !== undefined && (
            <p className="text-muted-foreground">
              {bubbleSizeKey}: {data[bubbleSizeKey]}
            </p>
          )}
        </div>
      );
    }

    return null;
  };

  const domain = parseDomain();
  const range = [20, 100]; // Sensible range for bubble sizes
  
  // Determine x-axis type based on data
  const xAxisType = (() => {
    // Check if we're using a time-based x-axis
    const isTimeField = ['year', 'date', 'month', 'quarter', 'day'].some(t => 
      xKey.toLowerCase().includes(t)
    );
    
    // For year fields, keep as number if numeric
    if (xKey.toLowerCase().includes('year') && typeof firstItem[xKey] === 'number') {
      return 'number';
    }
    
    // If all values are numeric, use number; otherwise use category
    if (chartData.every(item => typeof item[xKey] === 'number')) {
      return 'number';
    }
    
    return 'category';
  })();
  
  // Using the x-axis as the main dimension with multiple series
  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={400}>
        <ScatterChart
          margin={{
            top: 20,
            right: 20,
            bottom: 20,
            left: 20,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
          <XAxis
            dataKey={xKey}
            name={xKey}
            type={xAxisType as any}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tick={{ fill: "hsl(var(--muted-foreground))" }}
          />
          <YAxis
            dataKey={valueKey}
            name={valueKey}
            type="number"
            axisLine={{ stroke: "hsl(var(--border))" }}
            tick={{ fill: "hsl(var(--muted-foreground))" }}
          />
          <ZAxis
            dataKey={bubbleSizeKey}
            domain={domain}
            range={range}
            name={bubbleSizeKey}
          />
          <Tooltip 
            cursor={{ strokeDasharray: '3 3' }} 
            wrapperStyle={{ zIndex: 100 }} 
            content={tooltip?.content || CustomTooltip} 
          />
          <Legend />
          
          {Object.entries(seriesGroups).map(([series, groupData], index) => (
            <Scatter 
              key={`series-${series}`}
              name={series} 
              data={groupData} 
              fill={colors[index % colors.length]}
            />
          ))}
        </ScatterChart>
      </ResponsiveContainer>
      
      {/* Add suggestions list */}
      {shouldShowSuggestions && <SuggestionList suggestions={chartSuggestions} />}
    </div>
  );
};

export const HeatmapChartComponent: React.FC<{ config: ChartConfig; data: any[] }> = ({ config, data }) => {
  const { xAxisKey = "x", yAxisKey = "y", valueKey = "value", tooltip } = config;
  
  // Prepare data for heatmap rendering
  const processData = () => {
    // Create a map to track which cells have data
    const cellMap = new Map();
    
    // Get unique x and y values
    const xValues = Array.from(new Set(data.map(item => item[xAxisKey || "x"])));
    const yValues = Array.from(new Set(data.map(item => item[yAxisKey || "y"])));
    
    // Track min and max values
    let minValue = Infinity;
    let maxValue = -Infinity;
    
    // Process data and find min/max
    data.forEach(item => {
      const value = item[valueKey || "value"] || 0;
      minValue = Math.min(minValue, value);
      maxValue = Math.max(maxValue, value);
      
      // Create a unique key for each x,y combination
      const key = `${item[xAxisKey || "x"]},${item[yAxisKey || "y"]}`;
      cellMap.set(key, value);
    });
    
    return {
      xValues,
      yValues,
      cellMap,
      minValue: minValue === Infinity ? 0 : minValue,
      maxValue: maxValue === -Infinity ? 100 : maxValue
    };
  };
  
  const { xValues, yValues, cellMap, minValue, maxValue } = processData();
  
  // Calculate gradient color for a value
  const getColorIntensity = (value: number) => {
    const normalized = Math.max(0, Math.min(1, (value - minValue) / (maxValue - minValue || 1)));
    return normalized;
  };
  
  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
        <div className="flex items-center gap-1">
          <div className="h-3 w-3 bg-accent/20 rounded-sm"></div>
          <span>Low</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-3 w-3 bg-accent/80 rounded-sm"></div>
          <span>High</span>
        </div>
      </div>
      
      <div className="flex-1 grid rounded overflow-hidden" 
           style={{ 
             gridTemplateColumns: `repeat(${xValues.length}, 1fr)`,
             gridTemplateRows: `repeat(${yValues.length}, 1fr)`
           }}>
        {yValues.map((y, yIndex) => (
          xValues.map((x, xIndex) => {
            const key = `${x},${y}`;
            const value = cellMap.get(key) || 0;
            const intensity = getColorIntensity(value);
            
            return (
              <div
                key={`${xIndex}-${yIndex}`}
                className="relative flex items-center justify-center"
                style={{ 
                  backgroundColor: `hsla(var(--accent) / ${Math.max(0.1, intensity * 0.8)})`,
                  aspectRatio: "1/1"
                }}
                title={`${x}, ${y}: ${value}`}
              >
                <span className="text-[8px] text-foreground/80 font-mono">
                  {Math.round(value)}
                </span>
              </div>
            );
          })
        ))}
      </div>
      
      <div className="mt-2 flex justify-between items-center text-xs text-muted-foreground">
        <div className="text-xs truncate">
          {xAxisKey}: {xValues.length} values
        </div>
        <div className="text-xs truncate">
          {yAxisKey}: {yValues.length} values
        </div>
      </div>
    </div>
  );
};

export const FunnelChartComponent: React.FC<{ config: ChartConfig; data: any[] }> = ({ config, data }) => {
  const { nameKey = "name", valueKey = "value", tooltip } = config

  // Chart colors using CSS variables
  const chartColors = [
    "hsl(var(--chart-1))",
    "hsl(var(--chart-2))",
    "hsl(var(--chart-3))",
    "hsl(var(--chart-4))",
    "hsl(var(--chart-5))",
    "hsl(var(--chart-6))",
    "hsl(var(--chart-7))",
    "hsl(var(--chart-8))",

  ]

  // Extract suggestions if they exist in the config
  const chartSuggestions = config.suggestions || []
  const shouldShowSuggestions = chartSuggestions.length > 0 && config.show_suggestions !== false

  // Sort data by value in descending order for funnel visualization
  const sortedData = [...data].sort((a, b) => b[valueKey] - a[valueKey])

  // Calculate the maximum value for percentage calculations
  const maxValue = sortedData.length > 0 ? sortedData[0][valueKey] : 1

  // Create sophisticated gradient definitions with glass effects
  const renderGradients = () => {
    return sortedData.map((entry, index) => {
      const baseColor = chartColors[index % chartColors.length]
      const nextColor = chartColors[(index + 1) % chartColors.length]

      return (
        <React.Fragment key={`gradients-${index}`}>
          {/* Main gradient with glass effect */}
          <linearGradient id={`funnelGradient-${index}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={baseColor} stopOpacity={0.9} />
            <stop offset="30%" stopColor={baseColor} stopOpacity={0.7} />
            <stop offset="70%" stopColor={nextColor} stopOpacity={0.6} />
            <stop offset="100%" stopColor={nextColor} stopOpacity={0.4} />
          </linearGradient>

          {/* Reflection gradient */}
          <linearGradient id={`funnelReflection-${index}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="white" stopOpacity={0.3} />
            <stop offset="50%" stopColor="white" stopOpacity={0.1} />
            <stop offset="100%" stopColor="white" stopOpacity={0.05} />
          </linearGradient>
        </React.Fragment>
      )
    })
  }

  const formatValue = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`
    }
    return value.toString()
  }

  return (
    <div className="w-full space-y-6">
      {/* Chart Container with Glass Effect */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-background/50 to-muted/30 backdrop-blur-sm border border-foreground/10 p-6">
        <div className="absolute inset-0 bg-gradient-to-br from-foreground/5 to-transparent pointer-events-none" />

        <ResponsiveContainer width="100%" height={400}>
          <FunnelChart>
            <defs>
              {renderGradients()}
              {/* Global reflection overlay */}
              <linearGradient id="globalReflection" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="white" stopOpacity={0.2} />
                <stop offset="100%" stopColor="white" stopOpacity={0} />
              </linearGradient>
            </defs>

            <Tooltip
              contentStyle={{
                backgroundColor: "transparent",
                border: "none",
                padding: 0,
              }}
              content={tooltip?.content || CustomTooltip}
            />

            <Funnel
              dataKey={valueKey}
              nameKey={nameKey}
              data={sortedData}
              isAnimationActive={true}
              animationDuration={1200}
              animationBegin={0}
            >
              {sortedData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={`url(#funnelGradient-${index})`}
                  stroke="hsl(var(--accent))"
                  strokeWidth={0}
                  strokeOpacity={0}
                />
              ))}

              {/* Labels with better styling */}
              <LabelList
                position="right"
                fill="hsl(var(--foreground))"
                stroke="none"
                dataKey={nameKey}
                fontSize={10}
                fontWeight={300}
                offset={8}
              />

              <LabelList
                position="center"
                fill="hsl(var(--foreground))"
                stroke="none"
                fontSize={10}
                fontWeight="bold"
                formatter={(value: any) => `${((value / maxValue) * 100).toFixed(0)}%`}
                textAnchor="middle"
              />

              <LabelList
                position="left"
                fill="hsl(var(--muted-foreground))"
                stroke="none"
                fontSize={11}
                fontWeight={500}
                formatter={(value: any) => formatValue(value)}
                offset={8}
              />
            </Funnel>
          </FunnelChart>
        </ResponsiveContainer>
      </div>

      {/* Enhanced Conversion Metrics */}
      {sortedData.length > 1 && (
        <div className="space-y-4">
          {/* Header with glass effect */}
          <div className="flex justify-between items-center p-4 rounded-xl bg-gradient-to-r from-muted/20 to-muted/10 backdrop-blur-sm border border-white/10">
            <h4 className="text-sm font-semibold text-foreground/90">📊 Conversion Analysis</h4>
            <div className="px-3 py-1.5 rounded-full bg-gradient-to-r from-chart-1/20 to-chart-2/20 backdrop-blur-sm border border-white/10">
              <span className="text-xs font-medium text-foreground/80">Overall: </span>
              <span className="text-xs font-bold text-chart-1">
                {((sortedData[sortedData.length - 1][valueKey] / sortedData[0][valueKey]) * 100).toFixed(1)}%
              </span>
            </div>
          </div>

          {/* Metrics Grid with Enhanced Glass Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {sortedData.map((entry, index) => {
              if (index < sortedData.length - 1) {
                const nextEntry = sortedData[index + 1]
                const dropRate = (((entry[valueKey] - nextEntry[valueKey]) / entry[valueKey]) * 100).toFixed(1)
                const chartColor = chartColors[index % chartColors.length]

                return (
                  <div
                    key={`drop-${index}`}
                    className="group relative overflow-hidden rounded-lg bg-gradient-to-br from-background/60 to-muted/30 backdrop-blur-sm border border-white/10 p-3 transition-all duration-300 hover:shadow-lg hover:shadow-chart-1/10 hover:border-white/20"
                  >
                    {/* Gradient overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-chart-1/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    <div className="relative flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <div
                          className="w-2.5 h-2.5 rounded-full shadow-sm"
                          style={{
                            backgroundColor: chartColor,
                            boxShadow: `0 0 8px ${chartColor}40`,
                          }}
                        />
                        <span className="text-xs text-foreground/80 truncate font-medium">
                          {entry[nameKey]} → {nextEntry[nameKey]}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <span className="text-xs font-bold text-destructive/80">{dropRate}%</span>
                        <span className="text-xs text-muted-foreground">drop</span>
                      </div>
                    </div>
                  </div>
                )
              }
              return null
            })}
          </div>
        </div>
      )}

      {/* Enhanced Suggestions */}
      {shouldShowSuggestions && <SuggestionList suggestions={chartSuggestions} />}
    </div>
  )
}

// Boxplot Chart Component
export const BoxplotChartComponent: React.FC<{ config: ChartConfig; data: any[] }> = ({ config, data }) => {
  const { colors = DEFAULT_COLORS, variant = "default", tooltip, show_suggestions = false } = config
  const shouldShowSuggestions = show_suggestions === true
  
  // Chart suggestions for boxplot
  const chartSuggestions: ChartSuggestion[] = [
    {
      chartType: "bar",
      reason: "Bar charts are excellent for comparing categorical data and showing clear differences between groups.",
      title: "Bar Chart Comparison",
      query: "Show this data as a bar chart"
    },
    {
      chartType: "scatter",
      reason: "Scatter plots can reveal relationships and patterns in your data that boxplots might not show.",
      title: "Scatter Plot Analysis", 
      query: "Show this data as a scatter plot"
    },
    {
      chartType: "line",
      reason: "Line charts are perfect for showing trends over time if your data has a temporal component.",
      title: "Line Chart Trends",
      query: "Show this data as a line chart"
    }
  ]

  // Process data for boxplot - calculate quartiles, median, outliers
  const processBoxplotData = () => {
    if (!data || data.length === 0) return { boxplotData: [], chartData: [] }
    
    const { xAxisKey = Object.keys(data[0])[0], yAxisKeys = [] } = config
    
    // Group data by categories (x-axis)
    const groupedData: { [key: string]: number[] } = {}
    
    data.forEach(item => {
      const category = item[xAxisKey]
      if (!groupedData[category]) {
        groupedData[category] = []
      }
      
      // Collect all numeric values for this category
      yAxisKeys.forEach(key => {
        if (typeof item[key] === 'number') {
          groupedData[category].push(item[key])
        }
      })
    })
    
    // Calculate boxplot statistics for each category
    const boxplotData = Object.entries(groupedData).map(([category, values]) => {
      if (values.length === 0) return null
      
      const sorted = [...values].sort((a, b) => a - b)
      const q1Index = Math.floor(sorted.length * 0.25)
      const q3Index = Math.floor(sorted.length * 0.75)
      const medianIndex = Math.floor(sorted.length * 0.5)
      
      const q1 = sorted[q1Index]
      const q3 = sorted[q3Index]
      const median = sorted.length % 2 === 0 
        ? (sorted[medianIndex - 1] + sorted[medianIndex]) / 2
        : sorted[medianIndex]
      const min = sorted[0]
      const max = sorted[sorted.length - 1]
      const iqr = q3 - q1
      
      // Calculate outliers (values beyond 1.5 * IQR from quartiles)
      const lowerFence = q1 - 1.5 * iqr
      const upperFence = q3 + 1.5 * iqr
      const outliers = sorted.filter(v => v < lowerFence || v > upperFence)
      
      return {
        category,
        min,
        q1,
        median,
        q3,
        max,
        outliers,
        count: values.length
      }
    }).filter(Boolean)

    // Transform to stacked bar format (like the working example)
    const chartData = boxplotData.map(item => {
      if (!item) return null
      return {
        category: item.category,
        min: item.min,
        bottomWhisker: item.q1 - item.min,
        bottomBox: item.median - item.q1,
        topBox: item.q3 - item.median,
        topWhisker: item.max - item.q3,
        median: item.median,
        outliers: item.outliers,
        size: 50,
        // Store original data for tooltip
        originalData: item
      }
    }).filter(Boolean)

    return { boxplotData, chartData }
  }

  const { boxplotData, chartData } = processBoxplotData()
  const chartColors = colors.slice(0, boxplotData.length)

  // Horizontal line component for whisker caps and median
  const HorizontalLine = (props: any) => {
    const { x, y, width, height } = props
    if (x == null || y == null || width == null || height == null) {
      return null
    }
    return (
      <line 
        x1={x} 
        y1={y} 
        x2={x + width} 
        y2={y} 
        stroke={colors[0] || '#8884d8'} 
        strokeWidth={2} 
      />
    )
  }

  // Vertical line component for whiskers
  const VerticalLine = (props: any) => {
    const { x, y, width, height } = props
    if (x == null || y == null || width == null || height == null) {
      return null
    }
    return (
      <line
        x1={x + width / 2}
        y1={y + height}
        x2={x + width / 2}
        y2={y}
        stroke={colors[0] || '#8884d8'}
        strokeWidth={2}
      />
    )
  }

  return (
    <div className="w-full space-y-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-background/50 to-muted/30 backdrop-blur-sm border border-foreground/10 p-3 sm:p-4 lg:p-6">
        <div className="absolute inset-0 bg-gradient-to-br from-foreground/5 to-transparent pointer-events-none" />
        
        <ResponsiveContainer width="100%" height={350} className="sm:!h-[400px]">
          <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} barCategoryGap="40%">
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground))" strokeOpacity={0.3} />
            <XAxis 
              dataKey="category" 
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis 
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <ZAxis type="number" dataKey="size" range={[0, 100]} />
            
            {/* Stacked bars for boxplot - following the working example */}
            <Bar stackId="a" dataKey="min" fill="transparent" maxBarSize={40} />
            <Bar stackId="a" dataKey="bar" shape={<HorizontalLine />} maxBarSize={40} />
            <Bar stackId="a" dataKey="bottomWhisker" shape={<VerticalLine />} maxBarSize={40} />
            <Bar stackId="a" dataKey="bottomBox" fill={colors[0] || '#8884d8'} fillOpacity={0.6} maxBarSize={40} />
            <Bar stackId="a" dataKey="bar" shape={<HorizontalLine />} maxBarSize={40} />
            <Bar stackId="a" dataKey="topBox" fill={colors[0] || '#8884d8'} fillOpacity={0.6} maxBarSize={40} />
            <Bar stackId="a" dataKey="bar" shape={<HorizontalLine />} maxBarSize={40} />
            <Bar stackId="a" dataKey="topWhisker" shape={<VerticalLine />} maxBarSize={40} />
            
            {/* Scatter for outliers */}
            <Scatter dataKey="outliers" fill={colors[0] || '#8884d8'} />
            
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || !payload.length) return null
                
                // Get the original data from the payload
                const data = payload[0]?.payload?.originalData
                if (!data) return null
                
                return (
                  <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
                    <p className="font-medium mb-2">{label}</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">Max:</span>
                        <span className="font-medium">{data.max?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">Q3:</span>
                        <span className="font-medium">{data.q3?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">Median:</span>
                        <span className="font-medium">{data.median?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">Q1:</span>
                        <span className="font-medium">{data.q1?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">Min:</span>
                        <span className="font-medium">{data.min?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-muted-foreground">Count:</span>
                        <span className="font-medium">{data.count}</span>
                      </div>
                      {data.outliers?.length > 0 && (
                        <div className="flex justify-between gap-4">
                          <span className="text-muted-foreground">Outliers:</span>
                          <span className="font-medium">{data.outliers.length}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      
      {/* Statistics Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {boxplotData.map((item, index) => {
          if (!item) return null
          const cardColor = chartColors[index % chartColors.length] || colors[0]
          return (
            <div
              key={`stats-${index}`}
              className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-background via-background/95 to-muted/20 backdrop-blur-sm border border-border/50 hover:border-border transition-all duration-300 hover:shadow-lg hover:shadow-foreground/5"
            >
              {/* Accent bar */}
              <div 
                className="absolute top-0 left-0 right-0 h-1 opacity-80"
                style={{ backgroundColor: cardColor }}
              />
              
              {/* Content */}
              <div className="p-3 sm:p-4 lg:p-5">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h4 className="font-semibold text-sm sm:text-base text-foreground group-hover:text-foreground/90 transition-colors truncate">
                    {item.category}
                  </h4>
                  <div 
                    className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full opacity-80 flex-shrink-0"
                    style={{ backgroundColor: cardColor }}
                  />
                </div>
                
                <div className="space-y-2 sm:space-y-3">
                  {/* Primary stats */}
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="text-center p-2 rounded-lg bg-muted/30">
                      <div className="text-xs text-muted-foreground mb-1">Median</div>
                      <div className="font-bold text-xs sm:text-sm text-foreground">
                        {item.median.toLocaleString()}
                      </div>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-muted/30">
                      <div className="text-xs text-muted-foreground mb-1">Count</div>
                      <div className="font-bold text-xs sm:text-sm text-foreground">
                        {item.count}
                      </div>
                    </div>
                  </div>
                  
                  {/* Secondary stats */}
                  <div className="space-y-1.5 sm:space-y-2 text-xs">
                    <div className="flex justify-between items-center py-1">
                      <span className="text-muted-foreground flex items-center gap-1 text-xs">
                        <span className="w-1 h-1 rounded-full bg-muted-foreground/60"></span>
                        Range
                      </span>
                      <span className="font-medium text-foreground text-xs">
                        {(item.max - item.min).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-muted-foreground flex items-center gap-1 text-xs">
                        <span className="w-1 h-1 rounded-full bg-muted-foreground/60"></span>
                        IQR
                      </span>
                      <span className="font-medium text-foreground text-xs">
                        {(item.q3 - item.q1).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <span className="text-muted-foreground flex items-center gap-1 text-xs">
                        <span className="w-1 h-1 rounded-full bg-muted-foreground/60"></span>
                        Q1 - Q3
                      </span>
                      <span className="font-medium text-foreground text-xs">
                        {item.q1.toLocaleString()} - {item.q3.toLocaleString()}
                      </span>
                    </div>
                    {item.outliers.length > 0 && (
                      <div className="flex justify-between items-center py-1 mt-2 pt-2 border-t border-border/30">
                        <span className="text-muted-foreground flex items-center gap-1 text-xs">
                          <span className="w-1 h-1 rounded-full bg-orange-500/60"></span>
                          Outliers
                        </span>
                        <span className="font-medium text-orange-600 dark:text-orange-400 text-xs">
                          {item.outliers.length}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Subtle hover effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-foreground/0 via-foreground/0 to-foreground/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
            </div>
          )
        })}
      </div>
      
      {shouldShowSuggestions && <SuggestionList suggestions={chartSuggestions} />}
    </div>
  )
}

// Export all chart components at the end of the file, after all component definitions
export const chartComponentMap = {
  area: AreaChartComponent,
  bar: BarChartComponent,
  line: LineChartComponent,
  pie: PieChartComponent,
  radar: RadarChartComponent,
  radialBar: RadialBarChartComponent,
  treemap: TreeMapChartComponent,
  barBrush: BarChartWithBrushComponent,
  scatter: ScatterChartComponent,
  advancedScatter: AdvancedScatterChartComponent,
  bubblePlot: BubblePlotComponent,
  heatmap: HeatmapChartComponent,
  funnel: FunnelChartComponent,
  boxplot: BoxplotChartComponent
}